import React, { useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { FiBriefcase, FiCalendar, FiTrendingUp, FiStar, FiAward, FiFolder } from 'react-icons/fi';
import { Experience as ExperienceType } from '../types/resume.types';

interface ExperienceProps {
  experiences: ExperienceType[];
}

interface ExperienceCardProps {
  experience: ExperienceType;
  index: number;
  isLeft: boolean;
  onHoverChange?: (isHovered: boolean) => void;
}

const ExperienceCard: React.FC<ExperienceCardProps> = ({ experience, index, isLeft, onHoverChange }) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  const handleHover = (hovered: boolean) => {
    setIsHovered(hovered);
    onHoverChange?.(hovered);
  };

  return (
    <motion.div
      ref={cardRef}
      initial={{ x: isLeft ? -50 : 50, opacity: 0 }}
      whileInView={{ x: 0, opacity: 1 }}
      transition={{ duration: 0.6, delay: index * 0.2 }}
      viewport={{ once: true }}
      className="relative w-full"
      onMouseEnter={() => handleHover(true)}
      onMouseLeave={() => handleHover(false)}
    >
      <div className={`bg-white dark:bg-gray-800 p-8 rounded-2xl border border-gray-200 dark:border-gray-700 card-shadow hover:shadow-xl transition-all duration-300 relative group ${
        isHovered ? 'shadow-2xl transform scale-[1.02] border-primary-300 dark:border-primary-600' : ''
      }`}>
        {experience.type === 'current' && (
          <div className="absolute -top-3 -right-3">
            <div className="bg-accent-500 text-white px-3 py-1 rounded-full text-xs font-medium">
              Current
            </div>
          </div>
        )}
        
        <div>
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center space-x-4">
              <div className="w-12 h-12 bg-gradient-to-br from-primary-400 to-secondary-500 rounded-lg flex items-center justify-center">
                <FiBriefcase className="text-white" size={24} />
              </div>
              <div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                  {experience.position}
                </h3>
                <p className="text-primary-600 dark:text-primary-400 font-semibold">
                  {experience.company}
                </p>
              </div>
            </div>
            <div className="flex lg:hidden items-center space-x-2 text-gray-500 dark:text-gray-400">
              <FiCalendar size={16} />
              <span className="text-sm font-medium">{experience.duration}</span>
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                <FiTrendingUp className="mr-2 text-primary-500" size={18} />
                Key Responsibilities
              </h4>
              <ul className="space-y-2">
                {experience.responsibilities.map((responsibility, idx) => (
                  <motion.li
                    key={idx}
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.1 * idx }}
                    viewport={{ once: true }}
                    className="flex items-start space-x-3 text-gray-600 dark:text-gray-300"
                  >
                    <div className="w-2 h-2 bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                    <span>{responsibility}</span>
                  </motion.li>
                ))}
              </ul>
            </div>

            {experience.projects && experience.projects.length > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                  <FiFolder className="mr-2 text-primary-500" size={18} />
                  Projects
                </h4>
                <div className="space-y-4">
                  {experience.projects.map((project, idx) => (
                    <motion.div
                      key={idx}
                      initial={{ x: -20, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.1 * idx }}
                      viewport={{ once: true }}
                      className="bg-gradient-to-r from-primary-50 to-secondary-50 dark:from-primary-900/20 dark:to-secondary-900/20 p-4 rounded-lg ml-4"
                    >
                      <h5 className="font-semibold text-primary-700 dark:text-primary-300 mb-2">
                        {project.title}
                      </h5>
                      <ul className="space-y-1">
                        {project.description.map((desc, descIdx) => (
                          <li
                            key={descIdx}
                            className="flex items-start space-x-3 text-sm text-gray-600 dark:text-gray-300"
                          >
                            <div className="w-1.5 h-1.5 bg-primary-400 rounded-full mt-2 flex-shrink-0"></div>
                            <span>{desc}</span>
                          </li>
                        ))}
                      </ul>
                    </motion.div>
                  ))}
                </div>
              </div>
            )}

            {experience.featuredAssignments && experience.featuredAssignments.length > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                  <FiAward className="mr-2 text-accent-500" size={18} />
                  Featured Assignment
                </h4>
                {experience.featuredAssignments.map((assignment, idx) => (
                  <motion.div
                    key={idx}
                    initial={{ x: -20, opacity: 0 }}
                    whileInView={{ x: 0, opacity: 1 }}
                    transition={{ duration: 0.4, delay: 0.1 * idx }}
                    viewport={{ once: true }}
                    className="bg-gradient-to-r from-accent-50 to-primary-50 dark:from-accent-900/20 dark:to-primary-900/20 p-4 rounded-lg ml-4"
                  >
                    <div className="flex items-center justify-between mb-2">
                      <h5 className="font-semibold text-accent-700 dark:text-accent-300">
                        {assignment.title}
                      </h5>
                      <span className="text-xs text-gray-500 dark:text-gray-400 font-medium">
                        {assignment.duration}
                      </span>
                    </div>
                    <p className="text-sm text-accent-600 dark:text-accent-400 font-medium mb-2">
                      {assignment.company}
                    </p>
                    <p className="text-sm text-gray-600 dark:text-gray-300">
                      {assignment.description}
                    </p>
                  </motion.div>
                ))}
              </div>
            )}

            {experience.achievements && experience.achievements.length > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                <h4 className="font-semibold text-gray-900 dark:text-white mb-3 flex items-center">
                  <FiStar className="mr-2 text-accent-500" size={18} />
                  Key Achievements
                </h4>
                <ul className="space-y-2">
                  {experience.achievements.map((achievement, idx) => (
                    <motion.li
                      key={idx}
                      initial={{ x: -20, opacity: 0 }}
                      whileInView={{ x: 0, opacity: 1 }}
                      transition={{ duration: 0.4, delay: 0.1 * idx }}
                      viewport={{ once: true }}
                      className="flex items-start space-x-3 text-accent-700 dark:text-accent-300"
                    >
                      <FiStar className="mt-1 flex-shrink-0" size={14} />
                      <span className="font-medium">{achievement}</span>
                    </motion.li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

const TimelineEntry: React.FC<{ experience: ExperienceType; index: number }> = ({ experience, index }) => {
  const isLeft = index % 2 === 0;
  const [isCardHovered, setIsCardHovered] = useState(false);
  const [isDotHovered, setIsDotHovered] = useState(false);
  const entryRef = useRef<HTMLDivElement>(null);
  const isInView = useInView(entryRef, { once: true, amount: 0.3 });

  const isHighlighted = isCardHovered || isDotHovered;

  return (
    <div
      ref={entryRef}
      className="relative flex flex-col lg:grid lg:grid-cols-2 lg:gap-0 lg:items-center"
    >
      {/* Timeline dot with hover effect */}
      <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-20">
        <motion.div
          initial={{ scale: 0 }}
          whileInView={{ scale: 1 }}
          transition={{ duration: 0.4, delay: index * 0.2 }}
          viewport={{ once: true }}
          onMouseEnter={() => setIsDotHovered(true)}
          onMouseLeave={() => setIsDotHovered(false)}
          className={`w-6 h-6 bg-white dark:bg-gray-900 border-4 ${
            isHighlighted ? 'border-primary-500 scale-125' : 'border-primary-400'
          } rounded-full shadow-lg transition-all duration-300 cursor-pointer`}
        />
      </div>
      
      {/* Left side content */}
      <div className={`relative ${isLeft ? 'lg:pr-16' : 'lg:text-right lg:pr-16'}`}>
        {isLeft ? (
          <ExperienceCard 
            experience={experience} 
            index={index} 
            isLeft={isLeft}
            onHoverChange={setIsCardHovered}
          />
        ) : (
          /* Symmetrical date display for right-side cards */
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: index * 0.2 + 0.3 }}
            className="hidden lg:flex items-center justify-end h-full relative z-30"
          >
            <div className={`flex items-center space-x-3 transition-all duration-300 bg-gray-50 dark:bg-gray-900 px-2 ${
              isHighlighted ? 'scale-110 text-primary-600 dark:text-primary-400' : ''
            }`}>
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                {experience.duration}
              </span>
              <FiCalendar className="text-primary-400" size={18} />
            </div>
          </motion.div>
        )}
      </div>

      {/* Connector line from card to date label only */}
      {isLeft ? (
        /* Line from left card to date on the right side */
        <div className="hidden lg:block absolute top-1/2 transform -translate-y-1/2"
          style={{
            left: 'calc(50% - 4rem)',
            width: '4rem'
          }}
        >
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: index * 0.2, ease: 'easeOut' }}
            className={`h-0.5 ${
              isHighlighted ? 'bg-primary-500 h-1' : 'bg-primary-400'
            } transition-all duration-300`}
            style={{
              transformOrigin: 'left'
            }}
          />
        </div>
      ) : (
        /* Line from right card to date on the left side */
        <div className="hidden lg:block absolute top-1/2 transform -translate-y-1/2"
          style={{
            right: 'calc(50% - 4rem)',
            width: '4rem'
          }}
        >
          <motion.div
            initial={{ scaleX: 0 }}
            animate={isInView ? { scaleX: 1 } : {}}
            transition={{ duration: 0.8, delay: index * 0.2, ease: 'easeOut' }}
            className={`h-0.5 ${
              isHighlighted ? 'bg-primary-500 h-1' : 'bg-primary-400'
            } transition-all duration-300`}
            style={{
              transformOrigin: 'right'
            }}
          />
        </div>
      )}
      
      {/* Right side content */}
      <div className={`relative ${!isLeft ? 'lg:pl-16' : 'lg:text-left lg:pl-16'}`}>
        {!isLeft ? (
          <ExperienceCard 
            experience={experience} 
            index={index} 
            isLeft={isLeft}
            onHoverChange={setIsCardHovered}
          />
        ) : (
          /* Symmetrical date display for left-side cards */
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: index * 0.2 + 0.3 }}
            className="hidden lg:flex items-center justify-start h-full relative z-30"
          >
            <div className={`flex items-center space-x-3 transition-all duration-300 bg-gray-50 dark:bg-gray-900 px-2 ${
              isHighlighted ? 'scale-110 text-primary-600 dark:text-primary-400' : ''
            }`}>
              <FiCalendar className="text-primary-400" size={18} />
              <span className="text-sm font-semibold text-gray-600 dark:text-gray-400">
                {experience.duration}
              </span>
            </div>
          </motion.div>
        )}
      </div>
    </div>
  );
};

const Experience: React.FC<ExperienceProps> = ({ experiences }) => {
  return (
    <section id="experience" className="py-16 section-padding bg-gray-50 dark:bg-gray-800/50">
      <div className="container-width">
        <motion.div
          initial={{ y: 50, opacity: 0 }}
          whileInView={{ y: 0, opacity: 1 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Professional Experience
          </h2>
          <p className="text-gray-600 dark:text-gray-300 text-lg max-w-2xl mx-auto">
            A journey through automation testing, project leadership, and continuous learning
          </p>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-secondary-500 mx-auto mt-4"></div>
        </motion.div>

        <div className="relative max-w-6xl mx-auto">
          {/* Central timeline line */}
          <div className="absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-primary-400 via-secondary-400 to-accent-400 hidden lg:block"></div>
          
          <div className="space-y-16 lg:space-y-24">
            {experiences.map((experience, index) => (
              <TimelineEntry key={index} experience={experience} index={index} />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Experience;